import { request, requestData } from '@/lib/api-client';
import { ApiError } from '@/lib/errors';

/**
 * Notifications for new jobs, when the app is closed.
 *
 * The in-app toast (see `useNewJobAlerts`) only works while the app is open
 * and in front. A rider spends most of a shift with Maps in front, or the
 * phone in a holder, so the notification that matters most — "dispatch has
 * given you a job" — has to come through the operating system instead.
 *
 * The browser owns the permission, the service worker owns the display, and
 * the backend owns what is sent. This file only joins them up.
 */

export type PushState =
  /** This browser cannot do web push at all. */
  | 'unsupported'
  /** iPhone/iPad in a browser tab: push works only once added to the home screen. */
  | 'needs-install'
  /** The server has no VAPID key configured, so there is nothing to subscribe to. */
  | 'unavailable'
  /** Not asked yet. */
  | 'default'
  /** Refused, and only the rider can undo that, in the browser's settings. */
  | 'denied'
  /** On, and this install is registered with the server. */
  | 'enabled';

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Running as an installed app rather than a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  // iPadOS reports itself as a Mac; the touch points give it away.
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * Registers the service worker. Called once at startup; harmless to repeat.
 *
 * Failure is swallowed on purpose: the app works without it, just without
 * notifications and without the offline shell.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    return null;
  }
}

let cachedKey: string | null | undefined;

async function publicKey(): Promise<string | null> {
  if (cachedKey !== undefined) return cachedKey;
  try {
    const answer = await requestData<{ publicKey: string | null }>('/push/public-key');
    cachedKey = answer.publicKey;
  } catch (error) {
    // A server that predates push has no such route: that is "not available",
    // not a failure to show the rider. Anything else is rethrown.
    if (error instanceof ApiError && error.isNotFound) {
      cachedKey = null;
    } else {
      throw error;
    }
  }
  return cachedKey;
}

/** The URL-safe base64 the server hands out, as the bytes `subscribe` wants. */
function keyBytes(base64: string): Uint8Array<ArrayBuffer> {
  const padded = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function send(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  await request<void>('/me/push-subscriptions', {
    method: 'POST',
    body: { endpoint: json.endpoint, keys: json.keys },
  });
}

/** Where things stand, without asking the rider anything. */
export async function pushState(): Promise<PushState> {
  if (!isPushSupported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';

  let key: string | null;
  try {
    key = await publicKey();
  } catch {
    // Offline or the server is down: say "not asked" rather than "broken",
    // and let the next launch find out properly.
    return Notification.permission === 'granted' ? 'enabled' : 'default';
  }
  if (!key) return 'unavailable';

  if (Notification.permission !== 'granted') return 'default';
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  return existing ? 'enabled' : 'default';
}

/**
 * Asks, subscribes, and tells the server. Must run from a tap — browsers
 * refuse a permission prompt that is not the direct result of one.
 */
export async function enablePush(): Promise<PushState> {
  if (!isPushSupported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';

  const key = await publicKey();
  if (!key) return 'unavailable';

  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'denied';
  if (permission !== 'granted') return 'default';

  const registration = (await registerServiceWorker()) ?? (await navigator.serviceWorker.ready);
  await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(key),
    }));

  await send(subscription);
  return 'enabled';
}

/**
 * Re-registers an existing subscription with the server on every launch.
 *
 * Cheap, and it heals the cases nobody would notice otherwise: the push
 * service rotated the endpoint, the server's row was pruned after a failed
 * send, or a different rider signed in on this phone.
 */
export async function syncPushSubscription(): Promise<void> {
  if (!isPushSupported() || Notification.permission !== 'granted') return;
  try {
    const key = await publicKey();
    if (!key) return;
    const registration = await navigator.serviceWorker.ready;
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(key),
      }));
    await send(subscription);
  } catch {
    /* next launch tries again */
  }
}

/**
 * Stops this phone receiving jobs for this account. Called on sign-out, so a
 * shared phone does not go on buzzing with the previous rider's work.
 */
export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    try {
      await request<void>('/me/push-subscriptions', {
        method: 'DELETE',
        body: { endpoint: subscription.endpoint },
      });
    } finally {
      await subscription.unsubscribe();
    }
  } catch {
    /* best effort, like the rest of sign-out */
  }
}
